// Create / edit a weekly availability rule (recurring open hours).
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from '../../../components/shared/Modal';
import { createRule, updateRule } from '../callScheduleService';
import { DAY_NAMES } from '../utils/scheduleUtils';

const Field = styled.div`
  margin-bottom: 14px;
  label { display: block; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: ${p => p.theme.colors.textSecondary}; margin-bottom: 6px; }
  input, select {
    width: 100%; padding: 9px 12px; font-size: 0.9rem; box-sizing: border-box;
    background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
    border: 1.5px solid ${p => p.theme.colors.inputGray}44; border-radius: ${p => p.theme.borderRadius};
  }
  &.inline { display: flex; align-items: center; gap: 8px; }
  &.inline input { width: auto; }
  &.inline label { margin: 0; text-transform: none; letter-spacing: 0; font-size: 0.9rem; }
`;

const Row = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
`;

const ErrorText = styled.div`color: ${p => p.theme.colors.error}; font-size: 0.82rem; margin-bottom: 10px;`;

const RuleEditor = ({ isOpen, rule = null, onClose, onSaved }) => {
  const isEdit = !!rule;
  const [form, setForm] = useState({ day_of_week: 1, start_time: '09:00', end_time: '17:00', slot_minutes: 15, is_active: true });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    if (rule) {
      setForm({
        day_of_week: rule.day_of_week,
        start_time: rule.start_time,
        end_time: rule.end_time,
        slot_minutes: rule.slot_minutes || 15,
        is_active: rule.is_active,
      });
    } else {
      setForm({ day_of_week: 1, start_time: '09:00', end_time: '17:00', slot_minutes: 15, is_active: true });
    }
  }, [isOpen, rule]);

  const save = async () => {
    if (!form.start_time || !form.end_time) { setError('Start and end time are required.'); return; }
    if (form.end_time <= form.start_time) { setError('End time must be after start time.'); return; }
    try {
      const body = {
        day_of_week: Number(form.day_of_week),
        start_time: form.start_time,
        end_time: form.end_time,
        slot_minutes: Number(form.slot_minutes) || 15,
        is_active: form.is_active,
      };
      if (isEdit) await updateRule(rule.id, body);
      else await createRule(body);
      onSaved?.(isEdit ? 'Availability updated' : 'Availability added');
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save the rule.');
    }
  };

  if (!isOpen) return null;

  return (
    <Modal title={isEdit ? 'Edit Weekly Hours' : 'Add Weekly Hours'} isOpen={isOpen} onCancel={onClose} onConfirm={save}>
      {error && <ErrorText>{error}</ErrorText>}
      <Field>
        <label>Day of week</label>
        <select value={form.day_of_week} onChange={(e) => setForm(p => ({ ...p, day_of_week: e.target.value }))}>
          {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
        </select>
      </Field>
      <Row>
        <Field>
          <label>Opens</label>
          <input type="time" value={form.start_time} onChange={(e) => setForm(p => ({ ...p, start_time: e.target.value }))} />
        </Field>
        <Field>
          <label>Closes</label>
          <input type="time" value={form.end_time} onChange={(e) => setForm(p => ({ ...p, end_time: e.target.value }))} />
        </Field>
      </Row>
      <Field>
        <label>Slot length (minutes)</label>
        <input type="number" min="5" step="5" value={form.slot_minutes} onChange={(e) => setForm(p => ({ ...p, slot_minutes: e.target.value }))} />
      </Field>
      <Field className="inline">
        <input id="rule-active" type="checkbox" checked={form.is_active} onChange={(e) => setForm(p => ({ ...p, is_active: e.target.checked }))} />
        <label htmlFor="rule-active">Active (uncheck to disable without deleting)</label>
      </Field>
    </Modal>
  );
};

export default RuleEditor;
